import { SystemOptionType } from '../../enum';
import { EventType } from '../../lib';
import { metaFilter, isPromise } from '../ChatFramework.utils';
import { ChatFrameworkInterceptor } from '../ChatFramework.types';
import { ChatTriggerMatchSource, TriggerInterceptorOptions } from './ChatTrigger.types';
import { checkTrigger } from './ChatTrigger.checks';
import { isArticleFromListNavigationEvent } from '../ai-service/AiService.eventUtils';
import { ArticleListItemNavigationEvent, SystemOptionEvent } from '../../types';

function isCorrectEvent(evt: any): evt is SystemOptionEvent | ArticleListItemNavigationEvent {
  return evt && (EventType.isOptionEvent(evt) || isArticleFromListNavigationEvent(evt));
}

/**
 * Create an interceptor for chat messages that can check for triggers and
 * execute triggers.
 * NOTE: This only checks for article and section, this does not check for
 * article triggers as the triggers are checked on article page. This is due to a
 * limitation, in V2 chat framework, when clicked on an article, there is no article message
 * show, so there is no message to intercept.
 *
 * In future versions, we can tackle this via ChatFramework action Interceptors
 */
export function createTriggerInterceptor({ userCountry, onMatch }: TriggerInterceptorOptions) {
  const types = {
    [SystemOptionType.ARTICLE]: 'ARTICLE',
    [SystemOptionType.CATEGORY]: 'CATEGORY',
    [SystemOptionType.SECTION]: 'SECTION',
  } as const;

  const handler: ChatFrameworkInterceptor = {
    intercept(item, metadata) {
      const evt = metadata?.event;
      const tagsMatched = metaFilter(metadata?.tags)
        .all('from:system', 'fetching')
        .any('opt.article', 'opt.section', 'opt.category')
        .get();

      if (!isCorrectEvent(evt) || !isPromise(item) || !tagsMatched) {
        return item;
      }
      // When option was selected, and we are fetching the data for that option.
      const {
        payload: { selected: option },
      } = evt;

      const futureMessage = item;

      return Promise.all([
        futureMessage,
        checkTrigger(
          {
            type: types[option.kind] as ChatTriggerMatchSource,
            id: option.id,
            triggerEvent: evt,
            triggerMessage: evt.message,
            userCountry,
          },
          onMatch
        ),
      ]).then(([result, trigger]) => {
        if (!trigger) return result;
        // Wait for resolution, only then allow the article messages to be added to the queue.
        return trigger.waitForResolution().then(() => result);
      });
    },
  };

  return handler;
}
